/**
 * venture/RegDateBar.tsx — 7단계 「결성총회일」 줄 + 등록 서류 날짜 자동 점검 (2026-10-22 지도사 「날짜 체크 해야겠다 자동으로」)
 *  협회 보완 사유: 의사록 — 총회 개최 후 14일 이내 등록 신청 · LP 동의서 — 너무 오래전 날짜 반려
 *  보이는 것: 결성총회일 칸 · 「등록 신청 기한 10/31 · 5일 남음」 · 기한 지남이면 「오늘로 바꾸고 다시 만들기」 · 다른 날짜로 만든 서류가 있으면 「다시 만들기」
 */
import React, { useState } from 'react';
import { regenDocs } from './docgen';
import { GENDOCS } from './gendocs';
import { REG_KEYS, REG_LIMIT, checkRegDate, md, todayKst } from './regdate';
import type { Master, Project } from './types';

interface Props { p: Project; onPatch: (x: Partial<Project>) => void; toast: (m: string) => void; }

const TONE: Record<string, { bg: string; bd: string; ink: string }> = {
  late: { bg: 'var(--red-soft)', bd: '#FECACA', ink: 'var(--red)' },
  soon: { bg: 'var(--amber-soft)', bd: '#FED7AA', ink: '#9A3412' },
  none: { bg: 'var(--amber-soft)', bd: '#FED7AA', ink: '#9A3412' },
  ok: { bg: '#fff', bd: 'var(--line)', ink: 'var(--ink, #111827)' },
  future: { bg: '#fff', bd: 'var(--line)', ink: 'var(--ink, #111827)' },
};

export default function RegDateBar({ p, onPatch, toast }: Props) {
  const [busy, setBusy] = useState(false);
  const r = checkRegDate(p);
  const t = TONE[r.level];
  const short = (k: string) => (GENDOCS[k] ? GENDOCS[k].title.replace(/^개인투자조합 /, '').replace(/^GP /, '').replace(/ 수집·이용·제공 동의서$/, ' 동의서') : k);

  /** 결성총회일을 바꾸고(마스터) · 필요하면 등록 서류를 그 날짜로 다시 만든다 */
  const setDate = async (v: string, regen: string[]) => {
    if (!p.master || busy) return;
    const mm = JSON.parse(JSON.stringify(p.master)) as Master;
    mm.regApplyDate = v;
    onPatch({ master: mm });
    if (!regen.length) return;
    setBusy(true);
    try { await regenDocs({ ...p, master: mm }, regen, onPatch, toast); } catch { /* regenDocs 가 알림 */ } finally { setBusy(false); }
  };
  const regen = async (keys: string[]) => {
    if (!p.master || busy || !keys.length) return;
    setBusy(true);
    try { await regenDocs(p, keys, onPatch, toast); } catch { /* regenDocs 가 알림 */ } finally { setBusy(false); }
  };
  const made = (k: string) => { const l = (p.docver && p.docver[k]) || []; const v = l[l.length - 1]; return !!(v && (v.s3doc || v.s3pdf)); };
  const today = todayKst();

  return (
    <div className="uniqbar" style={{ background: t.bg, borderColor: t.bd }}>
      <label style={{ color: t.ink }}>결성총회일 <span>등록 서류(의사록 · 명부 · 출자증표 · 등록신청서 · 동의서) 날짜 · 총회 후 {REG_LIMIT}일 안에 등록 신청</span></label>
      <input type="date" value={r.date} disabled={!p.master || busy} onChange={(e) => { if (e.target.value) void setDate(e.target.value, []); }} />
      <div style={{ fontSize: 13, fontWeight: 700, color: t.ink }}>{r.text}</div>
      {r.level === 'late' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn pri sm" disabled={busy} onClick={() => void setDate(today, REG_KEYS.filter(made))}>
            {busy ? '다시 만드는 중…' : `오늘(${md(today)})로 바꾸고 등록 서류 다시 만들기`}
          </button>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>총회를 다시 열지 않았다면 실제 총회일로 고쳐 주세요</span>
        </div>
      )}
      {r.level !== 'late' && r.stale.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12.5, color: t.ink, fontWeight: 600 }}>다른 날짜로 만든 서류 {r.stale.length}종 · {r.stale.map(short).join(', ')}</span>
          <button className="btn pri sm" disabled={busy} onClick={() => void regen(r.stale)}>
            {busy ? '다시 만드는 중…' : `${md(r.date)} 날짜로 다시 만들기`}
          </button>
        </div>
      )}
      {r.staleSealed.length > 0 && <div style={{ fontSize: 12, color: 'var(--muted)' }}>날인본({r.staleSealed.map(short).join(', ')})은 다시 만든 서류로 날인 받아 올려 주세요</div>}
    </div>
  );
}
